import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { BigButton } from "../PublicShell";
import { plural } from "./diaryUtils";

const closesLabel = (iso) => new Date(new Date(iso).getTime() - 60000)
  .toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", timeZone: "Australia/Melbourne" });

/** "Send my diary now" with a confirm step; otherwise it is sent automatically when the window closes. */
export function DiaryFinish({ data, diary, pending, adapter, onDone, disabled }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const label = data.template.entry_label;
  const count = Object.values(diary.days || {}).reduce((n, d) => n + (d.entries?.length || 0), 0);
  const weekOver = data.today > data.calendar[data.calendar.length - 1].date;
  async function send() {
    setBusy(true);
    setErr(null);
    try {
      onDone(await adapter.submit(diary));
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Something went wrong — please try again.");
      setBusy(false);
    }
  }
  return (
    <section className="space-y-4 rounded-2xl border border-zinc-200 bg-white p-5" data-testid="diary-finish">
      <h2 className="font-display text-xl font-bold text-zinc-950">{weekOver ? "Your diary week is over" : "Finished your diary?"}</h2>
      <p className="text-base leading-relaxed text-zinc-700" data-testid="diary-closes">
        {weekOver && "Answer any recovery questions still waiting, then send it. "}
        Your diary is sent to your practitioner automatically at the end of <strong>{closesLabel(data.closes_at)}</strong>, or you can send it now.
      </p>
      {!confirm ? (
        <BigButton variant="secondary" className="w-full" onClick={() => setConfirm(true)} disabled={!count || disabled} data-testid="diary-finish-btn">
          I&apos;ve finished — send my diary
        </BigButton>
      ) : (
        <div className="space-y-3 rounded-xl bg-zinc-50 p-4" data-testid="diary-finish-confirm">
          <p className="text-base font-semibold text-zinc-950">Send {plural(count, label)} to your practitioner now? You won&apos;t be able to add or change entries after this.</p>
          {pending > 0 && <p className="text-base text-zinc-700" data-testid="diary-finish-pending">{plural(pending, "recovery question")} still blank — that&apos;s fine, they&apos;ll be recorded as not answered.</p>}
          <div className="flex flex-wrap gap-3">
            <BigButton onClick={send} disabled={busy} data-testid="diary-finish-confirm-btn">{busy ? "Sending…" : "Yes, send it now"}</BigButton>
            <BigButton variant="secondary" onClick={() => setConfirm(false)} disabled={busy} data-testid="diary-finish-cancel-btn">Keep going</BigButton>
          </div>
        </div>
      )}
      {!count && <p className="text-sm text-zinc-500" data-testid="diary-finish-empty">Add at least one {label} before sending.</p>}
      {err && <p className="text-base font-semibold text-red-700" role="alert" data-testid="diary-finish-error">{err}</p>}
    </section>
  );
}

export function DiaryDone({ result, copy }) {
  const flags = result.preview_flags;
  return (
    <div className="space-y-5" data-testid="diary-done">
      <CheckCircle2 className="h-12 w-12 text-emerald-700" aria-hidden="true" />
      <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">Thank you — your diary has been sent</h1>
      <p className="text-lg leading-relaxed text-zinc-800" data-testid="diary-done-message">{result.message || copy?.done_message}</p>
      {copy?.not_monitored && <p className="text-base leading-relaxed text-zinc-700">{copy.not_monitored}</p>}
      {flags && (
        <p className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 text-base text-amber-900" data-testid="diary-preview-flags">
          Staff note: {flags.length ? <>these entries would flag for the practitioner — <b>{flags.join("; ")}</b>.</> : "no flag rules would be met by these entries."}
        </p>
      )}
      <p className="text-base text-zinc-600">You can close this page.</p>
    </div>
  );
}
